<template>
  <div
      class="editable-element-drag-drop"
      :class="{ 'lock': elementInfo.lock }"
      :style="{
      width: '100%',
      top: elementInfo.top + 'px',
      left: elementInfo.left + 'px',

    }"
  >
    <div
        :class="cssClasses"
        ref="elementRef"
        :style="{
          height: elementInfo.vertical ? elementInfo.height + 'px' : '100%',
          backgroundColor: elementInfo.fill,
          opacity: elementInfo.opacity,
          textShadow: shadowStyle,
          lineHeight: elementInfo.lineHeight,
          letterSpacing: (elementInfo.wordSpace || 0) + 'px',
          color: elementInfo.defaultColor,
          // fontFamily: elementInfo.defaultFontName,
        }"
    >
      <div v-for="row in chosenLayout" :key="row.id" class="row draggable-row">
        <div
            v-for="child in row.children"
            :key="child.id"
            :class="{
                    'col': true,
                  }"
            :style="getContainerParentStyle(child)"
        >
          <div :style="getParentStyle(child)">
            <Draggable
                :id="`${elementInfo.id}-${child.id}`"
                :list="getDropZone(`${elementInfo.id}-${child.id}`)"
                :class="{
                    'widget-group': true
                  }"

                group="elements"
                itemKey="name"
                :disabled="disableDrag"
                :style="getSubsectionAlignment(child)"
            >
              <template #item="element">
                <div :style="getDimensionStyle(element.element.data)"
                     class="item-container">
                  <div class="widget-holder"
                       :id="`${element.element.data.id}`"
                       :style="getStyle(element.element.data)"
                  >
                    <component
                        :elementData="element.element.data"
                        :is="element.element.component"
                        :style="{ width: getStyle(element.element.data).justifyContent === 'space-between' ? '100%' : ''}"
                    />
                  </div>
                </div>
              </template>
            </Draggable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, markRaw, onBeforeMount, onMounted, PropType, ref, watch} from 'vue'
import {ChartOptions, DraggableElement, PPTDragDropElement} from '@/types/slides'
import {useMainStore, useSlidesStore} from '@/store'
import 'bootstrap/dist/css/bootstrap.css'
import Draggable from 'vuedraggable'
import CsvSelectorWidget from '@/views/components/element/DragDropElement/components/CsvSelectorWidget.vue'
import {useMicroservice, useCreateElement} from '@/hooks/index'
import RegularTextWidget from '@/views/components/element/DragDropElement/components/RegularTextWidget.vue'
import ImageWidget from "@/views/components/element/DragDropElement/components/ImageWidget.vue";
import ChartWidget from "@/views/components/element/ChartElement/index.vue"
import TableWidget from "@/views/components/element/TableElement/index.vue"
import ModelData from "@/views/components/element/DragDropElement/components/ModelDataWidget.vue"

import {storeToRefs} from 'pinia'
import {useWidgetStore} from '@/store/widgets'
import useElementShadow from '@/views/components/element/hooks/useElementShadow'
import useHistorySnapshot from "@/hooks/useHistorySnapshot";

const { addHistorySnapshot } = useHistorySnapshot()
const api = useMicroservice()

const props = defineProps({
  elementInfo: {
    type: Object as PropType<PPTDragDropElement>,
    required: true,
  },
  selectElement: {
    type: Function as PropType<(e: MouseEvent | TouchEvent, element: PPTDragDropElement, canMove?: boolean) => void>,
    required: true,
  },
  sectionId: {
    type: String,
    required: true,
  },
  sectionRowId: {
    type: String,
    required: true,
  },
})

const px = (input, defaultValue) => {
  if (input) {
    return `${input}px`
  }
  else {
    return defaultValue
  }
}

const getAttrNumber = (element, all, which) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return 0
  }
  return element?.style[all] ? element?.style[all] : element?.style[which]
}

const getAttr = (element, all, which) => {
  if (!element?.style || (!element?.style[all] && !element?.style[which])) {
    return 0
  }
  return element?.style[all] ? px(element?.style[all], 0) : px(element?.style[which], 0)
}

const highlightItem = (data) => {
  return selectedWidgets.value.includes(data)
}

const getPaddingWidth = (element) => {
  return getAttrNumber(element, 'padding', 'paddingLeft') + getAttrNumber(element, 'padding', 'paddingRight') +
      (highlightItem(element) ? 5 : 0)
}

const getPaddingHeight = (element) => {
  return getAttrNumber(element, 'padding', 'paddingTop') + getAttrNumber(element, 'padding', 'paddingBottom') +
      (highlightItem(element) ? 5 : 0)
}

const getMarginHeight = (element) => {
  return getAttrNumber(element, 'margin', 'marginTop') + getAttrNumber(element, 'margin', 'marginBottom')
}

const getMarginWidth = (element) => {
  return getAttrNumber(element, 'margin', 'marginLeft') + getAttrNumber(element, 'margin', 'marginRight')
}

const getDimensionStyle = (element) => {
  const borderColor = element?.style?.borderColor

  return {
    borderTop: `${getAttr(element, 'borderWidth', 'borderTop')} solid ${borderColor}`,
    borderRight: `${getAttr(element, 'borderWidth', 'borderRight')} solid ${borderColor}`,
    borderBottom: `${getAttr(element, 'borderWidth', 'borderBottom')} solid ${borderColor}`,
    borderLeft: `${getAttr(element, 'borderWidth', 'borderLeft')} solid ${borderColor}`,
    borderRadius: px(element?.style?.borderRadius, '0'),
    backgroundColor: element?.style?.backgroundColor,
    paddingTop: getAttr(element, 'padding', 'paddingTop'),
    paddingRight: getAttr(element, 'padding', 'paddingRight'),
    paddingBottom: getAttr(element, 'padding', 'paddingBottom'),
    paddingLeft: getAttr(element, 'padding', 'paddingLeft'),
    marginTop: getAttr(element, 'margin', 'marginTop'),
    marginRight: getAttr(element, 'margin', 'marginRight'),
    marginBottom: getAttr(element, 'margin', 'marginBottom'),
    marginLeft: getAttr(element, 'margin', 'marginLeft'),
    width: px(element?.style?.containerWidth || element?.style?.imageWidth ? (element?.style?.containerWidth || element?.style?.imageWidth) + getPaddingWidth(element) : null,`calc(100% - ${px(getMarginWidth(element), '0px')})`)
  }
}

const getSubsectionAlignment = (element) => {
  // we have to have a column flex so things get flipped
  let justifyContent = element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align
  if (justifyContent === 'left') {
    justifyContent = 'start'
  }
  else if (justifyContent === 'right') {
    justifyContent = 'end'
  }

  return {
    justifyContent: element?.style?.valign,
    alignItems: justifyContent
  }
}


const getContainerParentStyle = (element) => {
  const borderColor = element?.style?.borderColor


  return {
    margin: 0,
    padding: 0,
    display: 'flex',
    lineHeight: element?.style?.lineHeight ? element?.style?.lineHeight : 'normal',
    justifyContent: element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align,
    alignItems: element?.style?.valign,
    height: px((element?.style?.containerHeight || element?.style?.imageHeight)
        + getAttrNumber(element, 'margin', 'marginTop') + getAttrNumber(element, 'margin', 'marginBottom'),'auto'),
    width: px(element?.style?.containerWidth || element?.style?.imageWidth,'auto'),
  }
}

const getParentStyle = (element) => {
  const borderColor = element?.style?.borderColor


  return {
    marginTop: getAttr(element, 'margin', 'marginTop'),
    marginRight: getAttr(element, 'margin', 'marginRight'),
    marginBottom: getAttr(element, 'margin', 'marginBottom'),
    marginLeft: getAttr(element, 'margin', 'marginLeft'),
    paddingTop: getAttr(element, 'padding', 'paddingTop'),
    paddingRight: getAttr(element, 'padding', 'paddingRight'),
    paddingBottom: getAttr(element, 'padding', 'paddingBottom'),
    paddingLeft: getAttr(element, 'padding', 'paddingLeft'),
    display: 'flex',
    lineHeight: element?.style?.lineHeight ? element?.style?.lineHeight : 'normal',
    justifyContent: element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align,
    alignItems: element?.style?.valign,
    borderTop: `${getAttr(element, 'borderWidth', 'borderTop')} solid ${borderColor}`,
    borderRight: `${getAttr(element, 'borderWidth', 'borderRight')} solid ${borderColor}`,
    borderBottom: `${getAttr(element, 'borderWidth', 'borderBottom')} solid ${borderColor}`,
    borderLeft: `${getAttr(element, 'borderWidth', 'borderLeft')} solid ${borderColor}`,
    borderRadius: px(element?.style?.borderRadius, '0'),
    backgroundColor: element?.style?.backgroundColor,
    height: px(element?.style?.containerHeight,'auto'),
    width: px(element?.style?.containerWidth,'auto'),
  }
}

const getStyle = (element) => {
  return {
    textAlign: element?.style?.align,
    display: 'flex',
    lineHeight: element?.style?.lineHeight ? element?.style?.lineHeight : 'normal',
    justifyContent: element?.style?.justifyContent !== '' ? element?.style?.justifyContent : element?.style?.align,
    alignItems: element?.style?.valign,
    height: px(element?.style?.containerHeight,'100%'),
    width: px(element?.style?.containerWidth,'100%'),
  }
}

const cssClasses = ref('element-content dragdrop')

const {savePresentation} = useMicroservice()

const reactiveJson = ref(null)
const elementRef = ref<HTMLElement | null>(null)

const mainStore = useMainStore()
const {activeDropzone, editModeOn, toolbarState} = storeToRefs(mainStore)
const widgetStore = useWidgetStore()
const { selectedWidgets, selectedWidgetGroupId, multiSelect, activeType, selectedSectionId, selectedSectionRowId, selectedSubsectionId, selectedSubsectionRowId } = storeToRefs(widgetStore)
const slidesStore = useSlidesStore()
const {slides, slideName } = storeToRefs(slidesStore)
const chosenLayout = ref([])

const eventDetected = ref(false)
let dragEvent: any = null
let eventProcessing = false
let previousWidget: any = null
let editMode = true
const disableDrag = ref(false)

const shadow = computed(() => props.elementInfo.shadow)
const {shadowStyle} = useElementShadow(shadow)

const hideEmptyDropzone = computed(() => editModeOn.value ?'visible': 'hidden')

const componentTypes = {
  'reg_text': markRaw(RegularTextWidget),
  'model_data': markRaw(ModelData),
  'csv_selector': markRaw(CsvSelectorWidget),
  'image': markRaw(ImageWidget),
  'chart': markRaw(ChartWidget),
  'table': markRaw(TableWidget),
}

onBeforeMount(async () => {
  const findDragDrop: PPTDragDropElement = <PPTDragDropElement>slidesStore.getElementsByType('dragdrop').find((zone) => zone.id === props.elementInfo?.id)
  reactiveJson.value = findDragDrop ? findDragDrop.widgets : []
})

onMounted(() => {
  chosenLayout.value = props.elementInfo.layout
})

watch(() => props.elementInfo.layout, (newLayout) => {
  chosenLayout.value = newLayout;
}, { deep: true });

watch(eventDetected, () => {
  if (!eventProcessing) {
    dropToolboxWidget(dragEvent)
  }
})

watch(reactiveJson, async (newVal) => {
  slidesStore.updateElement({id: props.elementInfo.id, props: {widgets: newVal}})
  await savePresentation()
}, {deep: true})


const getDropZone = ((parentId) => {
  try {
    // can't do equals here because we used to also have sublayouts that no longer exist
    const parentExists = reactiveJson.value.filter(item => item.parent.indexOf(parentId) === 0)
    if (parentExists.length === 0) {
      reactiveJson.value = [...reactiveJson.value, {
        'parent': parentId,
        'children': []
      }]
    }

    return replaceTextWithComponents(parentExists ? parentExists.flatMap(obj => obj.children) : []);
  } catch(e) {
    console.log('catch', e)
  }
})

const replaceTextWithComponents = ((children) => {
  const formattedChildren = []
  children.forEach((item) => {
    const component = componentTypes[item.type]
    formattedChildren.push({
      data: item,
      component
    })
  })

  return formattedChildren
})


const dropToolboxWidget = (async (event) => {
  if (!eventProcessing) {
    eventProcessing = true
    event.stopImmediatePropagation()
    const {target, component, elementId, index} = event.detail
    if (target) await addDraggableElement(target, component, elementId, index)
    eventProcessing = false
  }
})

const addDraggableElement = (target, component, elementId, index) => {
  return new Promise((resolve) => {

    const targetEl: {
      parent: string;
      children: DraggableElement[]
    } | undefined = reactiveJson.value.find((item) => item.parent === target)


    let newDraggableElement: DraggableElement = {
      id: elementId,
      type: component,
      subType: 'out_text',
      hookId: '',
      compact: false,
      iconPosition: 'none',
      text: component === 'reg_text'? 'Here is your new text block.': 'Select data from Value Model',
      step: 1,
      currency: 'USD',
      locale: 'en-US',
      csv: {},
      style: {
        borderRadius: component === 'model_data' ? 25 : 0,
        fontSize: '20px'
      },
      chartType: component === 'chart' ? 'pie' : '',
      imageUrl: '',
      min: 0,
      max: 100
    }


    newDraggableElement.style['padding'] = 25


    if (component === 'chart') {
      const chart = createChartWidget(component)
      newDraggableElement.style['containerHeight'] = 500
      newDraggableElement.style['containerWidth'] = 600
      newDraggableElement = { ...newDraggableElement, ...chart }
    }

    if (component === 'table') {
      const table = useCreateElement().createTableElement(2, 5)
      newDraggableElement = { ...newDraggableElement, ...table, id: newDraggableElement.id }
      delete table.id;
    }

    if (targetEl) {
      targetEl.children.splice(index, 0, newDraggableElement)
      widgetStore.setActiveWidget(newDraggableElement)
    }
    resolve()
  })
}

const createChartWidget = (type: string) => {
  const newElement = {
    left: 300,
    top: 81.25,
    width: 400,
    height: 400,
    rotate: 0,
    themeColor: ['#5b9bd5'],
    gridColor: 'rgb(230, 230, 230)',
    data: {
      labels: [],
      legends: [],
      series: [
        ['Value Driver 1', 12000],
        ['Value Driver 2', 19000],
      ],
      hookIds: []
    },
    labelStyle: {
      fontSize: '12px'
    },
  }

  let options: ChartOptions = {}
  if (type === 'horizontalBar') options = { horizontalBars: true }
  else if (type === 'area') options = { showArea: true }
  else if (type === 'scatter') options = { showLine: false }
  else if (type === 'ring') options = { donut: true }

  return {
    ...newElement,
    options,
  }
}
</script>

<style lang="scss" scoped>


.draggable-row {
  --bs-gutter-x: 0;
  --bs-gutter-y: 0;
}

.element-content.dragdrop.active-dropzone {
  border: dashed 5px rgb(53, 150, 243);
  position: absolute;
}

.widget-group {

  min-height: 30px;

  color-scheme: light;
  user-select: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;
}

.widget-holder {
  position: relative;
  display: inline-block;
  justify-content: center;
  align-items: center;
  background-color: transparent;
}

.close {
  float: right;
}

.close:hover {
  cursor: pointer;
}

.editable-element-drag-drop {

  &.lock .element-content {
    cursor: default;
  }
}

.rotate-wrapper {
  width: 100%;
  height: 100%;
}


.drag-handler {
  height: 10px;
  position: absolute;
  left: 0;
  right: 0;

  &.top {
    top: 0;
  }

  &.bottom {
    bottom: 0;
  }
}

.item-container {
  border-radius: 10px;
  border: solid transparent 1px;
  justify-content: center;
  display: flex;
  flex-direction: row;
}



.subsection {
  border: 1px solid rgba(255, 165, 0,.5);
}



</style>
